// TranstionUpdate.jsx  ← FULL FINAL VERSION (Copy-Paste This)
import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../../creds";
import { toast } from "react-toastify";
import { Document, Packer, Paragraph, TextRun } from "docx";
import { saveAs } from "file-saver";
import { IoMdArrowRoundBack } from "react-icons/io";
import { useParams, useNavigate } from "react-router-dom";
import TransliterateInput from "../common/TransliterateInput";

const PrintOptions = ({ responseData }) => {
  const [selectedFormat, setSelectedFormat] = useState("");

  const handlePrint = (format) => {
    if (!responseData?.trim()) {
      toast.error("No translation to print.");
      setSelectedFormat("");
      return;
    }

    try {
      if (format === "txt") {
        const blob = new Blob([responseData], { type: "text/plain;charset=utf-8" });
        saveAs(blob, "translation.txt");
      } else if (format === "doc") {
        const doc = new Document({
          sections: [{
            children: [new Paragraph({ children: [new TextRun(responseData)] })],
          }],
        });
        Packer.toBlob(doc).then((blob) => saveAs(blob, "translation.docx"));
      }
      setSelectedFormat("");
    } catch (err) {
      toast.error("Print failed");
      setSelectedFormat("");
    }
  };

  return (
    <select
      className="bg-white border border-gray-300 rounded-lg shadow-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 pl-8 pr-4 py-2 text-sm"
      value={selectedFormat}
      onChange={(e) => handlePrint(e.target.value)}
    >
      <option value="">Print Translation</option>
      <option value="txt">TXT File</option>
      <option value="doc">Word DOC</option>
    </select>
  );
};

const TranstionUpdate = () => {
  const { taskId } = useParams();                    // e.g. "123456789abc"
  const navigate = useNavigate();

  const [task, setTask] = useState(null);
  const [fileContent, setFileContent] = useState("");
  const [responseData, setResponseData] = useState("");
  const [sourceLang, setSourceLang] = useState("en");
  const [targetLang, setTargetLang] = useState("ur");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [latestVersion, setLatestVersion] = useState("");

  const access_token = localStorage.getItem("access_token");

  // Load task when component mounts
  useEffect(() => {
    const loadTask = async () => {
      if (!taskId || !access_token) {
        setError("Invalid task or not logged in");
        setLoading(false);
        return;
      }

      try {
        const res = await axios.get(`${API_URL}/get_task/${taskId}`, {
          headers: { Authorization: `Bearer ${access_token}` },
        });

        const data = res.data;
        setTask(data);
        setFileContent(data.source_text || "");
        setResponseData(data.target_text || "");
        setSourceLang(data.source_language || "en");
        setTargetLang(data.target_language || "ur");
        setLatestVersion(data.latest_version);

      } catch (err) {
        console.error(err);
        setError("Failed to load task. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadTask();
  }, [taskId, access_token]);

  // SAVE + SHOW BOTH ALERT & TOAST + REFRESH LIST DATA
  const handleSave = async () => {
    setSaving(true);
    setError("");

    try {
      // eslint-disable-next-line
      const response = await axios.post(
        `${API_URL}/update_task/${taskId}`,
        {
          source_text: fileContent,
          target_text: responseData,
          latest_version: latestVersion
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${access_token}`,
          },
        }
      );

      // SUCCESS MESSAGES — EXACTLY LIKE BEFORE + TOAST
      toast.success("Translation updated successfully!");
      alert("Translation updated successfully!");   // YOUR OLD ALERT BACK

      // FORCE LIST TO SHOW UPDATED DATA (clears any old cache)
      sessionStorage.removeItem("tasks");           // Most apps cache here
      localStorage.removeItem("tasksList");         // In case you use localStorage
      navigate("/tasks");
    } catch (err) {
      console.error("Save error:", err);
      toast.error("Failed to save changes");
      setError("Failed to save. Check your connection and try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => navigate("/tasks");

  if (loading) return <div className="p-10 text-center text-xl">Loading task...</div>;
  if (error && !task) return <div className="p-10 text-center text-red-600">{error}</div>;

  // Format ID nicely: 1234-5678-9012-3456
  const formatId = (id) =>
    `${id.slice(0, 4)}-${id.slice(4, 8)}-${id.slice(8, 12)}-${id.slice(12)}`;

  return (
    <>
      <div className="p-6 max-w-7xl mx-auto min-h-screen">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            Edit Translation – {formatId(taskId)}
          </h2>
          <button
            onClick={handleBack}
            disabled={saving}
            className="flex items-center gap-2 bg-gray-500 hover:bg-gray-600 text-white px-5 py-2 rounded-lg transition disabled:opacity-60"
          >
            <IoMdArrowRoundBack size={26} />
            Back to List
          </button>
        </div>

        {/* Error / Saving */}
        {error && <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4">{error}</div>}
        {saving && <div className="text-blue-600 font-medium mb-4">Saving changes...</div>}

        {/* Textareas */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <TransliterateInput
            value={fileContent}
            onChange={setFileContent}
            targetLang={sourceLang}
            isUrdu={sourceLang}
            placeholder="Start typing in Source – suggestions appear below"
          />

          <TransliterateInput
            value={responseData}
            onChange={setResponseData}
            targetLang={targetLang}
            isUrdu={targetLang}
            placeholder="Start typing in English – suggestions appear below"
          />

        </div>

        {/* Save + Print Buttons */}
        <div className="flex justify-end gap-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition disabled:opacity-60"
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>

          <PrintOptions responseData={responseData} />
        </div>
      </div>
    </>
  );
};

export default TranstionUpdate;